import type { TFunction } from "i18next";

import { errors, TRANSLATION_KEY, TRANSLATION_PARAMS_KEY } from "../constants";
import { localizePermissionTranslationParams } from "../dto/common/simple-result.translate.helpers";
import {
  finishedProcessTypes,
  ProcessStatusEnum,
  RydeProcessTitle,
} from "../enums";
import { isEntityAdminPathMapped } from "../mappers/entity.admin.mappings";
import { isEntityPathMapped } from "../mappers/entity.path.mappings";
import type {
  IRydeProcess,
  ITranslatableItem,
  RydeAppUnit,
  RydeUiUnit,
  TActiveProcessKey,
  TTranslationParams,
} from "../models";
import { isBulkTranslatedErrorsResult } from "./api-utils";
import { Log } from "./log.helpers";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isResultTranslatableItem = (item: unknown): item is ITranslatableItem =>
  isRecord(item) && typeof item[TRANSLATION_KEY] === "string";

export interface IBulkActionResult {
  success: boolean;
  error?: unknown;
  data?: unknown;
}

export const getActiveProcessKey = (
  processTitle: RydeProcessTitle,
  entity: RydeUiUnit,
): TActiveProcessKey => `${processTitle}_${entity}`;

export const getChunkErrorsFromResult = (
  result: IBulkActionResult,
): ITranslatableItem[] => {
  if (!isRecord(result.data) || !("errors" in result.data)) {
    return [];
  }
  const { errors: maybeErrors } = result.data;
  if (!Array.isArray(maybeErrors)) {
    return [];
  }
  return maybeErrors.filter(isResultTranslatableItem);
};

export const isBulkAllSkippedResult = (result: IBulkActionResult): boolean =>
  !result.success &&
  isResultTranslatableItem(result.error) &&
  result.error[TRANSLATION_KEY] === errors.ride.bulkAllRidesSkipped;

export const isProcessCanceledByUser = (
  process: Pick<IRydeProcess, "errors"> | null | undefined,
): boolean =>
  Boolean(
    process?.errors?.some(
      item =>
        isResultTranslatableItem(item) &&
        item[TRANSLATION_KEY] === errors.processes.processCanceledByUser,
    ),
  );

export const excludeProcessCanceledByUserErrors = (
  processErrors: ITranslatableItem[] | undefined,
): ITranslatableItem[] | undefined => {
  if (!processErrors?.length) {
    return processErrors;
  }

  const filtered = processErrors.filter(
    item =>
      !isResultTranslatableItem(item) ||
      item[TRANSLATION_KEY] !== errors.processes.processCanceledByUser,
  );

  return filtered.length ? filtered : undefined;
};

export interface IHandleProcessArguments {
  entity: RydeUiUnit;
  process: IRydeProcess | null;
  translationFunction: TFunction<"translation", undefined>;
  processTitle: RydeProcessTitle;
  stopProcessPolling: ({
    id,
    entity,
    processTitle,
  }: {
    id: number;
    processTitle: RydeProcessTitle;
    entity: RydeUiUnit;
  }) => void;
  onFail?: (process: IRydeProcess) => void;
  onComplete?: (process: IRydeProcess) => void;
  isSkipToast?: boolean;
  toastSummaryOnComplete?: boolean;
}

export interface IProcessBulkCounts {
  totalCount: number;
  successCount: number;
  skippedCount: number;
  warningCount: number;
}

export interface IGroupedProcessError {
  translationKey: string;
  rideIds: number[];
  translationParams?: TTranslationParams;
}

const BULK_SUMMARY_TOAST_EXCLUDED_APP_ENTITIES = new Set<RydeAppUnit>([
  "user",
  "unknown",
  "dashboard",
  "report",
  "rideMap",
  "tracking",
  "view",
]);

const BULK_SUMMARY_TOAST_ADMIN_ENTITIES = new Set<RydeUiUnit>(["busStation"]);

export const supportsBulkSummaryToast = (entity: RydeUiUnit): boolean => {
  if (isEntityAdminPathMapped(entity)) {
    return BULK_SUMMARY_TOAST_ADMIN_ENTITIES.has(entity);
  }

  if (!isEntityPathMapped(entity)) {
    return false;
  }

  return !BULK_SUMMARY_TOAST_EXCLUDED_APP_ENTITIES.has(entity);
};

const toEntityId = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

/** Bulk skip errors should carry the affected row in `id` or `recordId`; rides may still use `rideId`. */
const extractSkippedEntityId = (
  params: Record<string, unknown> | undefined,
): number | undefined =>
  toEntityId(params?.id) ??
  toEntityId(params?.recordId) ??
  toEntityId(params?.rideId) ??
  toEntityId(params?.bookingId) ??
  toEntityId(params?.passengerId) ??
  toEntityId(params?.supplierId) ??
  toEntityId(params?.areaId);

const parseIdListParam = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value.flatMap(item => parseIdListParam(item));
  }

  if (typeof value === "string") {
    return value
      .split(/[,\s]+/)
      .map(part => toEntityId(part))
      .filter((id): id is number => id !== undefined);
  }

  const single = toEntityId(value);
  return single !== undefined ? [single] : [];
};

const extractSkippedEntityIds = (
  params: Record<string, unknown> | undefined,
): number[] => {
  const ids: number[] = [];
  const singleId = extractSkippedEntityId(params);

  if (singleId !== undefined) {
    ids.push(singleId);
  }

  if (params?.ids !== undefined) {
    ids.push(...parseIdListParam(params.ids));
  }

  return [...new Set(ids)];
};

const SKIPPED_ENTITY_ID_PARAM_KEYS = new Set([
  "id",
  "recordId",
  "rideId",
  "bookingId",
  "passengerId",
  "supplierId",
  "areaId",
  "ids",
]);

const toReasonTranslationParams = (
  params: Record<string, unknown> | undefined,
): TTranslationParams | undefined => {
  if (!params) {
    return undefined;
  }

  const reasonParams: TTranslationParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (SKIPPED_ENTITY_ID_PARAM_KEYS.has(key)) {
      continue;
    }
    if (typeof value === "string" || typeof value === "number") {
      reasonParams[key] = value;
    }
  }

  return Object.keys(reasonParams).length ? reasonParams : undefined;
};

export const buildSkippedIdTranslationParams = (
  ids: string,
): TTranslationParams => ({
  id: ids,
  recordId: ids,
  rideId: ids,
  bookingId: ids,
  passengerId: ids,
  supplierId: ids,
  areaId: ids,
  rideIds: ids,
  bookingIds: ids,
  passengerIds: ids,
  supplierIds: ids,
  areaIds: ids,
});

const countGroupedProcessErrorIds = (groups: IGroupedProcessError[]): number =>
  groups.reduce((count, group) => count + (group.rideIds.length || 1), 0);

/**
 * One translatable error can sit on a successful row (draft fallback, skipped
 * manual price note). Split grouped rows by successIds so the same id is not
 * counted as both skip and success: intersection → warnings, rest → skipped.
 */
export const splitGroupedProcessErrorsBySuccessIds = (
  groups: IGroupedProcessError[],
  successIds: number[] | undefined,
): { skipped: IGroupedProcessError[]; warnings: IGroupedProcessError[] } => {
  const successIdSet = new Set(successIds ?? []);
  const skipped: IGroupedProcessError[] = [];
  const warnings: IGroupedProcessError[] = [];

  for (const group of groups) {
    if (!group.rideIds.length) {
      skipped.push(group);
      continue;
    }

    const warningIds = group.rideIds.filter(id => successIdSet.has(id));
    const skippedIds = group.rideIds.filter(id => !successIdSet.has(id));

    if (skippedIds.length) {
      skipped.push({ ...group, rideIds: skippedIds });
    }
    if (warningIds.length) {
      warnings.push({ ...group, rideIds: warningIds });
    }
  }

  return { skipped, warnings };
};

export const groupProcessErrorsByReason = (
  translatableErrors: ITranslatableItem[] | undefined,
): IGroupedProcessError[] => {
  if (!translatableErrors?.length) {
    return [];
  }

  const grouped = new Map<
    string,
    {
      translationKey: string;
      rideIds: number[];
      translationParams?: TTranslationParams;
    }
  >();

  for (const error of translatableErrors) {
    const translationKey = error[TRANSLATION_KEY];
    if (!translationKey) {
      continue;
    }
    const params = error[TRANSLATION_PARAMS_KEY] as
      Record<string, unknown> | undefined;
    const entityIds = extractSkippedEntityIds(params);
    const reasonParams = toReasonTranslationParams(params);
    const groupingKey = `${translationKey}|${JSON.stringify(reasonParams ?? {})}`;
    const existing = grouped.get(groupingKey) ?? {
      translationKey,
      rideIds: [],
      translationParams: reasonParams,
    };
    entityIds.forEach(entityId => {
      if (!existing.rideIds.includes(entityId)) {
        existing.rideIds.push(entityId);
      }
    });
    grouped.set(groupingKey, existing);
  }

  return [...grouped.values()].map(
    ({ translationKey, rideIds, translationParams }) => ({
      translationKey,
      rideIds,
      ...(translationParams ? { translationParams } : {}),
    }),
  );
};

export const getProcessBulkCounts = (
  process: Pick<IRydeProcess, "affectedIds" | "metadata" | "errors">,
): IProcessBulkCounts => {
  const successIds = process.metadata?.metrics?.successIds;
  const failedIds = process.metadata?.metrics?.failedIds;
  const successCount = Array.isArray(successIds) ? successIds.length : 0;
  const successIdSet = new Set(Array.isArray(successIds) ? successIds : []);
  const displayErrors = excludeProcessCanceledByUserErrors(process.errors);
  const { skipped: skippedGroups, warnings: warningGroups } =
    splitGroupedProcessErrorsBySuccessIds(
      groupProcessErrorsByReason(displayErrors),
      Array.isArray(successIds) ? successIds : [],
    );
  const warningCount = countGroupedProcessErrorIds(warningGroups);
  // Prefer failedIds, but drop ids that also succeeded so Selected is not
  // success + skip of the same row.
  const skippedCount = Array.isArray(failedIds)
    ? failedIds.filter(id => !successIdSet.has(id)).length
    : countGroupedProcessErrorIds(skippedGroups);
  const totalCount = process.affectedIds?.length ?? successCount + skippedCount;

  return { totalCount, successCount, skippedCount, warningCount };
};

export const formatGroupedProcessErrorLine = (
  group: IGroupedProcessError,
  translationFunction: TFunction<"translation", undefined>,
): string => {
  const rideIds = group.rideIds.join(", ");
  const reason = translationFunction(group.translationKey, {
    ...buildSkippedIdTranslationParams(rideIds),
    ...localizePermissionTranslationParams(
      group.translationKey,
      group.translationParams,
      translationFunction,
    ),
  });

  if (!rideIds) {
    return reason;
  }

  return `${reason} (${translationFunction("process.skippedIds", { ids: rideIds })})`;
};

export const toastProcessBulkSummary = ({
  process,
  translationFunction,
}: {
  process: IRydeProcess;
  translationFunction: TFunction<"translation", undefined>;
}): void => {
  const { totalCount, successCount, skippedCount, warningCount } =
    getProcessBulkCounts(process);
  const relatedHubRideIds = process.metadata?.metrics?.relatedHubRideIds;
  const relatedHub =
    relatedHubRideIds?.length &&
    translationFunction("process.bulkSummaryRelatedHub", {
      relatedRideIds: relatedHubRideIds.join(", "),
    });

  const appendHub = (message: string): string =>
    relatedHub ? `${message} ${relatedHub}` : message;

  if (skippedCount === 0 && warningCount === 0) {
    Log.successAndToast(
      appendHub(
        translationFunction("process.bulkSummaryAllSuccess", {
          successCount,
          totalCount,
        }),
      ),
    );
    return;
  }

  if (skippedCount === 0) {
    Log.warnAndToast(
      appendHub(
        translationFunction("process.bulkSummaryAllSuccessWithWarnings", {
          successCount,
          totalCount,
          warningCount,
        }),
      ),
    );
    return;
  }

  if (successCount === 0) {
    Log.warnAndToast(
      appendHub(
        translationFunction("process.bulkSummaryAllSkipped", {
          skippedCount,
          totalCount,
        }),
      ),
    );
    return;
  }

  Log.warnAndToast(
    appendHub(
      translationFunction(
        warningCount
          ? "process.bulkSummaryPartialWithWarnings"
          : "process.bulkSummaryPartial",
        {
          successCount,
          skippedCount,
          totalCount,
          warningCount,
        },
      ),
    ),
  );
};

export const toastGroupedProcessErrors = ({
  errors: translatableErrors,
  translationFunction,
}: {
  errors: ITranslatableItem[] | undefined;
  translationFunction: TFunction<"translation", undefined>;
}): void => {
  const grouped = groupProcessErrorsByReason(translatableErrors);
  if (!grouped.length) {
    return;
  }

  Log.warnAndToast(
    grouped
      .map(group => formatGroupedProcessErrorLine(group, translationFunction))
      .join("\n"),
  );
};

export const applyBulkAllSkippedFeedback = ({
  result,
  translationFunction,
  setErrors,
}: {
  result: IBulkActionResult;
  translationFunction: TFunction<"translation", undefined>;
  setErrors?: (messages: string[]) => void;
}): boolean => {
  if (!isBulkAllSkippedResult(result)) {
    return false;
  }

  const chunkErrors = getChunkErrorsFromResult(result);
  toastGroupedProcessErrors({
    errors: chunkErrors,
    translationFunction,
  });
  setErrors?.(
    groupProcessErrorsByReason(chunkErrors).map(group =>
      formatGroupedProcessErrorLine(group, translationFunction),
    ),
  );
  return true;
};

export const handleProcess = ({
  process,
  entity,
  processTitle,
  stopProcessPolling,
  translationFunction,
  onFail,
  onComplete,
  isSkipToast = false,
  toastSummaryOnComplete = false,
}: IHandleProcessArguments): void => {
  if (process === null || process.status === ProcessStatusEnum.Pending) {
    return;
  }

  if (finishedProcessTypes.includes(process.status)) {
    stopProcessPolling({
      id: process.id,
      entity,
      processTitle,
    });
  }

  if (process.status === ProcessStatusEnum.Failed) {
    if (isProcessCanceledByUser(process)) {
      if (!isSkipToast) {
        Log.infoAndToast(translationFunction("process.aborted"));
        const successIds = process.metadata?.metrics?.successIds;
        if (successIds?.length && toastSummaryOnComplete) {
          toastProcessBulkSummary({ process, translationFunction });
        }
      }

      if (process.metadata?.metrics?.successIds?.length) {
        onComplete?.(process);
      }

      return;
    }

    if (isBulkTranslatedErrorsResult(process.errors) && !isSkipToast) {
      toastGroupedProcessErrors({
        errors: process.errors,
        translationFunction,
      });
    }

    onFail?.(process);
  }

  if (process.status === ProcessStatusEnum.Completed) {
    if (toastSummaryOnComplete && !isSkipToast) {
      toastProcessBulkSummary({ process, translationFunction });
    }
    onComplete?.(process);
  }
};
